import { API_URL } from './api'; // Your base API URL
import { clearLocalStorageKeepingLoginHint } from '../config/authStorageKeys';

/**
 * A helper function to handle fetch requests, based on your api.js reference.
 * It includes error handling, 401 redirection, and 204 (No Content) management.
 */

const fetchWithHandling = async (url, options) => {
  try {
    options.credentials = 'include';
    const response = await fetch(url, options);

    if (response.status === 401) {
      // Handle token expiry
      clearLocalStorageKeepingLoginHint();
      window.location.href = '/login';
      throw new Error('Unauthorized: Token expired or invalid.');
    }

    if (!response.ok) {
      // For non-OK responses, try to parse a JSON error body
      const errorDetails = await response.json().catch(() => ({}));
      throw new Error(
        errorDetails.error ||
          errorDetails.detail ||
          errorDetails.message ||
          `Error: ${response.status} ${response.statusText}`,
      );
    }

    if (response.status === 204) {
      // For DELETE requests or other "No Content" success responses
      return;
    }

    // For all other successful (200, 201) responses
    return response.json();
  } catch (error) {
    throw error; // Propagate the error for the component to catch
  }
};

export const deleteMeeting = async (token, meetingId) => {
  return fetchWithHandling(`${API_URL}/meetings/${meetingId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const processMeetingEvent = async (token, meetingId, eventId) => {
  return fetchWithHandling(
    `${API_URL}/meetings/${meetingId}/events/${eventId}/process`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // No body is needed, but you could add one if required
    },
  );
};

// --- User Endpoint ---
export const listUsers = async (token) => {
  return fetchWithHandling(`${API_URL}/users/`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
};

/**
 * The glance view for one meeting event: headline, counts, and chart series.
 *
 * Computed server-side from records already committed — no model call, so it
 * is cheap enough to fetch on every open and can never disagree with the
 * records it describes.
 */
export const getMeetingDigest = async (token, eventId) => {
  return fetchWithHandling(`${API_URL}/meetings/events/${eventId}/digest`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
};

/**
 * One step from a transcript file to a meeting being processed: the server
 * creates the meeting, its event and the transcript and queues it.
 */
export const uploadTranscript = async (
  token,
  file,
  { title, date, startTime, attendees = [] } = {},
) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (title) formData.append('title', title);
  if (date) formData.append('date', date);
  if (startTime) formData.append('start_time', startTime);
  if (attendees.length) formData.append('attendees', attendees.join(','));
  return fetchWithHandling(`${API_URL}/meetings/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
};

// --- One list: Teams meetings and meetings added here ---
export const listAllMeetings = async (token) =>
  fetchWithHandling(`${API_URL}/meetings/list`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

export const retryTeamsMeeting = async (token, eventId) =>
  fetchWithHandling(
    `${API_URL}/meetings/teams/${encodeURIComponent(eventId)}/process`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );

export const hideTeamsMeeting = async (token, eventId) =>
  fetchWithHandling(
    `${API_URL}/meetings/teams/${encodeURIComponent(eventId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
